"use client";

import { Loading } from "../../../../ui/loading";
import { smoothScroll } from "../../utils/smoothScroll";
import { getTopDistanceForFlexReverse } from "@/utils/scroll";
import { cn } from "@deputy-ai/common";
import { useCallbackRef } from "@radix-ui/react-use-callback-ref";
import {
  type UIEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useImperativeHandle,
  type ComponentProps,
  type Ref,
} from "react";
import type { Merge } from "type-fest";

export interface ScrollToEndOptions {
  animated?: boolean;
  force?: boolean;
}

interface PTRScrollToOptions extends ScrollToEndOptions {
  y: number | "100%";
}

export interface ScrollToRefreshHandler {
  scrollTo: (opts: PTRScrollToOptions) => void;
  scrollToEnd: (opts?: ScrollToEndOptions) => void;
  wrapperRef: RefObject<HTMLDivElement>;
}

export type UiScrollToRefreshProps<T> = Merge<
  Omit<ComponentProps<"div">, "children">,
  {
    ref?: Ref<ScrollToRefreshHandler>;
    list: T[];
    renderItem: (item: T) => ReactNode;
    renderIndicator?: () => ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    refreshThreshold?: number;
    disabledRefresh?: boolean;
    onRefresh?: () => void;
  }
>;

export function UiScrollToRefresh<T>(props: UiScrollToRefreshProps<T>) {
  const {
    ref,
    list,
    renderItem,
    header,
    footer,
    refreshThreshold = 50,
    disabledRefresh = false,
    onScroll,
    onRefresh,
    renderIndicator = () => {
      return <Loading className="h-6 w-6" />;
    },
    className,
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null!);

  const scrollTo = useCallbackRef(
    ({ y, animated = true }: PTRScrollToOptions) => {
      const scroller = wrapperRef.current;

      if (!scroller) {
        return;
      }

      const offsetTop =
        y === "100%" ? scroller.scrollHeight - scroller.offsetHeight : y;

      if (animated) {
        smoothScroll({
          el: scroller,
          to: offsetTop,
          x: false,
        });
        return;
      }

      scroller.scrollTop = offsetTop;
    }
  );

  const scrollToEnd = useCallbackRef(
    ({ animated = true }: ScrollToEndOptions = {}) => {
      scrollTo({ y: "100%", animated });
    }
  );

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    onScroll?.(e);
    if (disabledRefresh) {
      return;
    }
    const container = e.target as HTMLDivElement;

    const topDistance = getTopDistanceForFlexReverse(container);
    if (topDistance > refreshThreshold) {
      return;
    }
    onRefresh?.();
  }

  useImperativeHandle(
    ref,
    () => ({
      scrollTo,
      scrollToEnd,
      wrapperRef,
    }),
    []
  );

  const items = [];

  for (let i = list.length - 1; i >= 0; i--) {
    const itemEl = renderItem(list[i]);
    items.push(itemEl);
  }

  return (
    <div
      ref={wrapperRef}
      onScroll={handleScroll}
      className={cn(
        "flex h-full flex-col-reverse items-center overflow-y-auto px-1.5",
        className
      )}
    >
      <div className="flex-1"></div>
      {footer}
      {items}
      {!disabledRefresh && (
        <div className="flex shrink-0 items-center justify-center">
          {renderIndicator()}
        </div>
      )}
      {header}
    </div>
  );
}
